import React, { useState } from "react";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { DollarSign, Globe, Rocket, TrendingUp, Target, Users, Zap, Heart, BarChart3, CheckCircle } from "lucide-react";

export default function Careers() {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "hiring_updates"), {
        email,
        timestamp: new Date(),
        source: "careers_page",
      });
      setIsSubscribed(true);
      setEmail("");
      setTimeout(() => setIsSubscribed(false), 5000);
    } catch (error) {
      console.error("Error subscribing: ", error);
      alert("An error occurred. Please try again.");
    }
  };

  const futureRoles = [
    {
      title: "Full-Stack Developer",
      department: "Engineering",
      description:
        "Build and scale our platform as we grow from hundreds to thousands of users",
      skills: ["React Native", "Node.js", "Firebase", "Real-time Systems", "API Design"],
      status: "Future Opening",
    },
    {
      title: "Growth Marketing Manager",
      department: "Marketing",
      description:
        "Drive user acquisition and scale our community from early adopters to mass market",
      skills: ["Growth Hacking", "Analytics", "SEO/SEM", "Content Strategy", "Community Building"],
      status: "Future Opening",
    },
    {
      title: "Product Designer (UI/UX)",
      department: "Design",
      description:
        "Craft intuitive, delightful experiences that drive engagement and retention",
      skills: ["UI/UX Design", "Figma", "User Research", "Prototyping", "Mobile Design"],
      status: "Future Opening",
    },
    {
      title: "Business Development Lead",
      department: "Partnerships",
      description:
        "Build strategic partnerships with restaurants, chef associations, and culinary organizations",
      skills: ["Partnership Strategy", "Sales", "Negotiation", "Relationship Management", "B2B"],
      status: "Future Opening",
    },
  ];

  const perks = [
    { Icon: DollarSign, title: "Competitive Equity", description: "Early team members receive meaningful equity packages" },
    { Icon: Globe, title: "Remote-First", description: "Work from anywhere with flexible hours" },
    { Icon: Rocket, title: "Ground Floor Impact", description: "Shape the product, culture, and direction of the company" },
    { Icon: TrendingUp, title: "Growth Opportunities", description: "Grow your career as we scale the business" },
    { Icon: Target, title: "Mission-Driven", description: "Work on something meaningful that connects people" },
    { Icon: Users, title: "Collaborative Culture", description: "Work directly with founders and have real influence" },
  ];

  const values = [
    { title: "Move Fast, Build Smart", description: "We iterate quickly but thoughtfully, shipping features that matter to users.", Icon: Zap },
    { title: "User Obsession", description: "Every decision starts with understanding what our users truly need", Icon: Heart },
    { title: "Data-Driven", description: "We let metrics and user feedback guide our product decisions", Icon: BarChart3 },
    { title: "Own Your Work", description: "Take ownership, make decisions, and see the direct impact of your contributions", Icon: Target },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange/5">
      <div className="min-h-screen flex flex-col items-center p-8">
        {/* Success Message */}
        {isSubscribed && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 flex items-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            You're on the list! We'll notify you when we start hiring.
          </motion.div>
        )}

        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center max-w-4xl mt-16 mb-20"
        >
          <motion.h1
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            className="text-5xl font-bold text-gray-900 mb-6"
          >
            Join Us When We're <span className="text-orange">Ready to Scale</span>
          </motion.h1>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            We're a bootstrapped startup focused on validating Compliments to the Chef. Right now, we're not hiring, but when we're ready to grow our team, we want to connect with exceptional talent like you.
          </p>

          {/* Status Banner */}
          <div className="bg-orange/10 border-2 border-orange/40 rounded-xl p-6 mb-8 max-w-2xl mx-auto shadow-md">
            <div className="flex items-center justify-center mb-3">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3 animate-pulse"></div>
              <h3 className="text-gray-900 font-bold text-lg">Current Status: Not Actively Hiring</h3>
            </div>
            <p className="text-gray-700 text-sm">
              We're currently in our growth and validation phase, scaling our user base and proving product-market fit. Once we hit key milestones and secure funding, we'll be ready to bring on our first hires.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              disabled
              className="bg-gray-200 text-gray-500 px-8 py-3 rounded-lg cursor-not-allowed font-medium opacity-70"
            >
              No Open Positions
            </button>
            <button
              onClick={() =>
                document.getElementById("notify-form").scrollIntoView({ behavior: "smooth" })
              }
              className="bg-orange text-white px-8 py-3 rounded-lg hover:bg-orange-dark transition duration-300 font-medium"
            >
              Get Notified When We Hire
            </button>
          </div>
        </motion.section>

        {/* Journey Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="w-full max-w-6xl mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              We're building something special. Here's where we are and where we're headed.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { stage: "DONE", color: "text-gray-400", title: "Soft Launch", desc: "Validated our MVP with real users", tag: "✓ COMPLETED" },
              { stage: "NOW", color: "text-orange", title: "Growth & Validation", desc: "Scaling users and proving market fit", tag: "✓ IN PROGRESS" },
              { stage: "NEXT", color: "text-purple-600", title: "Team Building", desc: "Hiring our first team members", tag: "2026" },
              { stage: "FUTURE", color: "text-yellow-600", title: "Scale & Fund", desc: "Raising capital and expanding", tag: "2026" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-gray-200 p-6 rounded-xl shadow-md hover:shadow-lg transition"
              >
                <div className={`${item.color} font-bold text-sm mb-2`}>{item.stage}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-700 text-sm mb-3">{item.desc}</p>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-orange/10 text-orange">
                  {item.tag}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Future Roles */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="w-full max-w-6xl mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Roles We'll Eventually Hire</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              When we're ready to scale, these are the kinds of roles we'll be looking to fill.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {futureRoles.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 p-8 rounded-xl shadow-md hover:shadow-lg relative"
              >
                <div className="absolute top-4 right-4">
                  <span className="bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full">
                    {role.status}
                  </span>
                </div>
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{role.title}</h3>
                  <p className="text-orange text-sm font-semibold">{role.department}</p>
                </div>
                <p className="text-gray-700 mb-6">{role.description}</p>
                <div>
                  <h4 className="text-gray-900 font-semibold text-sm mb-3">
                    Key Skills We'll Look For:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {role.skills.map((skill, i) => (
                      <span
                        key={i}
                        className="bg-orange/10 text-gray-800 text-xs px-3 py-1 rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Perks */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="w-full max-w-6xl mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Join When We're Hiring?
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              Early team members don't just get a job; they get to build something from the ground up.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {perks.map((perk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white border border-gray-200 p-6 rounded-xl shadow-md hover:shadow-lg text-center"
              >
                <perk.Icon className="w-10 h-10 mb-4 mx-auto text-orange" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{perk.title}</h3>
                <p className="text-gray-700 text-sm">{perk.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Values */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="w-full max-w-6xl mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How We Work</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              The values and principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-gray-200 p-6 rounded-xl shadow-md hover:shadow-lg"
              >
                <value.Icon className="w-8 h-8 mb-3 text-orange" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-700 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Notify Form */}
        <motion.section
          id="notify-form"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="w-full max-w-2xl mb-20"
        >
          <div className="bg-orange/10 border-2 border-orange/30 rounded-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Watch This Space</h2>
              <p className="text-gray-700 text-lg">
                We're not hiring now, but we will be soon. Leave your email and we'll notify you when positions open up.
              </p>
            </div>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="flex-1 px-6 py-4 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-orange focus:outline-none"
              />
              <button
                type="submit"
                className="bg-orange text-white px-8 py-4 rounded-lg hover:bg-orange-dark transition duration-300 font-medium whitespace-nowrap"
              >
                Notify Me
              </button>
            </form>

            <p className="text-gray-500 text-sm mt-4 text-center">
              No spam. Just a heads-up when we start hiring. Unsubscribe anytime.
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
}